/* eslint-disable no-nested-ternary */

import Reset from "components/buttons/Reset"
import getColorGrades from "helpers/getColorGrades"
import { lighten, rgba } from "polished"
import styled from "styled-components"
import colors from "styles/colors"
import motionTokens from "styles/motionTokens"
import { Palette, Theme } from "styles/theme"

interface Props {
  $bold?: boolean
  $color?: keyof Palette
  $expanded?: boolean
  $hasIconEnd: boolean
  $hasIconStart: boolean
  $iconOnly?: boolean
  $loading?: boolean
  $noHoverHighlight?: boolean
  $noPadding?: boolean
  $outlined?: boolean
  $padded?: boolean
  $raised?: boolean
  $rounded?: boolean
  $secondary?: boolean
  $small?: boolean
  $transparent?: boolean
  disabled?: boolean
}

const BACKGROUND_ALPHA = 0.2
const BOX_SHADOW_ALPHA_ONE = 0.2
const BOX_SHADOW_ALPHA_TWO = 0.14
const BOX_SHADOW_ALPHA_THREE = 0.12

const getBackground = (
  props: Props & { theme: Theme },
  isLighten?: boolean
) => {
  let background: string

  if (props.$color) {
    background = props.theme.palette[props.$color].main
  } else if (props.$transparent) {
    background = "transparent"
  } else {
    const colorGrades = getColorGrades(props)

    background = props.$outlined ? colorGrades.contrast : colorGrades.main
  }

  return isLighten ? lighten(BACKGROUND_ALPHA, background) : background
}

const getBorder = (props: Props & { theme: Theme }, isLighten?: boolean) => {
  let border: string

  if (props.$color) {
    border = props.theme.palette[props.$color].main
  } else if (props.$transparent) {
    border = "1px solid transparent"
  } else {
    const colorGrades = getColorGrades(props)

    border = `1px solid ${
      isLighten ? lighten(BACKGROUND_ALPHA, colorGrades.main) : colorGrades.main
    }`
  }

  return border
}

const getColor = (props: Props & { theme: Theme }, isLighten?: boolean) => {
  let color: string

  const colorGrades = getColorGrades(props)

  if (props.$outlined || props.$transparent) {
    color = colorGrades.main
  } else {
    color = colorGrades.contrast
  }

  return isLighten ? lighten(BACKGROUND_ALPHA, color) : color
}

export const Wrapper = styled(Reset).attrs((props: Props) => ({
  "data-variant": props.$secondary
    ? "secondary"
    : props.$outlined
      ? "outlined"
      : props.$iconOnly
        ? "iconOnly"
        : undefined
}))`
  align-items: center;
  background: ${getBackground};
  border: ${getBorder};
  border-radius: ${({ $rounded }: { $rounded?: boolean }) =>
    $rounded ? "50%" : "3px"};
  ${({ $raised }: { $raised?: boolean }) =>
    $raised &&
    `box-shadow: 0px 3px 1px -2px ${rgba(
      colors.black,
      BOX_SHADOW_ALPHA_ONE
    )}, 0px 2px 2px 0px ${rgba(
      colors.black,
      BOX_SHADOW_ALPHA_TWO
    )}, 0px 1px 5px 0px ${rgba(colors.black, BOX_SHADOW_ALPHA_THREE)};`}
  color: ${getColor};
  column-gap: 10px;
  cursor: ${({ disabled }: { disabled?: boolean }) =>
    disabled ? "not-allowed" : "pointer"};
  display: grid;
  font-family: ${({ theme }: { theme: Theme }) => theme.font.family};
  font-size: ${({ $small }: { $small?: boolean }) =>
    $small ? "14px" : "16px"};
  font-weight: ${({ $bold }: { $bold?: boolean }) => ($bold ? "700" : "300")};
  grid-template-columns: ${({ $hasIconEnd, $hasIconStart, $loading }: Props) =>
    $loading
      ? "auto auto auto auto"
      : $hasIconEnd && $hasIconStart
        ? "auto auto auto"
        : $hasIconEnd || $hasIconStart
          ? "auto auto auto"
          : "auto"};
  justify-items: center;
  line-height: 1.33;
  padding: ${({ $padded, $noPadding, $rounded, $small, $iconOnly }: Props) =>
    $noPadding
      ? "0"
      : $small
        ? "6px 12px"
        : ($iconOnly && !$padded) || $rounded
          ? "10px"
          : $padded
            ? "15px 42px"
            : "10px 20px"};
  pointer-events: ${({ disabled }: { disabled?: boolean }) =>
    disabled ? "none" : "auto"};
  transition: ${motionTokens.duration.short}ms ${motionTokens.easing.standard};
  transition-property: background, background-color, color, opacity;
  width: ${({ $expanded }: { $expanded?: boolean }) =>
    $expanded ? "100%" : "auto"};

  &:hover {
    ${(props: Props & { theme: Theme }) =>
      !props.$noHoverHighlight &&
      `background:
      ${
        props.disabled || props.$transparent
          ? getBackground(props)
          : getBackground(props, true)
      };
    border: ${
      props.disabled || props.$transparent
        ? getBorder(props)
        : getBorder(props, true)
    };
    color: ${
      props.disabled || props.$transparent
        ? getColor(props)
        : getColor(props, true)
    };
    }`}

    & svg {
      transform: scale(${motionTokens.scale.medium});
    }
  }

  &:hover,
  &:focus-visible {
    outline: none;
  }

  & i,
  & svg {
    color: currentColor;
  }

  & svg {
    transition: transform ${motionTokens.duration.short}ms
      ${motionTokens.easing.standard};
    transform-origin: center;
    will-change: transform;
  }

  &:active svg {
    transform: scale(${motionTokens.press.medium});
  }

  &[aria-disabled="true"] svg {
    transform: none;
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:focus-visible,
    &:active {
      svg {
        transform: none;
      }
    }
  }
`
